import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, ButtonBase, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { CORAL_100, CORAL_600, GRAY_900, GREEN_100, GREEN_600, DARK_ORANGE, YELLOW_100 } from 'constants/styling/theme'
import { FC, ReactNode } from 'react'
import { PropertyOrderStatusStep, RealEstatePropertyOrderStatusDTO } from 'models/property'
import { SettledReorderContext, embedReorderClickProps, withReorderContext } from 'utils/analytics/reorderClick'
import { formatPrice, valueMultiplied } from 'utils/price'

import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { DealDTOClient } from 'models/deal'
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import { OrganizationProductKinds } from 'constants/product'
import moment from 'moment'
import { startOrderAttempt } from 'utils/analytics/orderAttempt'
import { useTranslation } from 'react-i18next'

/** Page actions surfaced to the client; kept in sync with the analytics `action` values. */
type PageAction = 'NEW_ORDER' | 'RESCHEDULE' | 'CANCEL'

/** Steps at which the reschedule/cancel actions are still meaningful (order not yet in production). */
const EDITABLE_STEPS: PropertyOrderStatusStep[] = [
  PropertyOrderStatusStep.ORDER_PLACED,
  PropertyOrderStatusStep.APPOINTMENT_AGREED,
]

/**
 * @interface Props Input properties
 */
interface Props {
  /** The order (status projection) the page acts on. */
  order: RealEstatePropertyOrderStatusDTO
  /** The property's CRM id from the iframe URL; with the order id, it joins the click to the order's server events. */
  objectReferenceId: string | null
  /** The full deal for the order, fetched by the controller and shared with the cancel popup. */
  deal: DealDTOClient | undefined
  /** Whether the shared deal query is still pending. */
  isDealPending: boolean
  /** Whether the shared deal query failed; renders an inline error instead of an endless spinner. */
  isDealError?: boolean
  /** Starts a new order for the same property. */
  onNewOrder: () => void
  /** What the next order on the property would be, for the new-order click: at once when cached, null when unknown. */
  reorderContext: () => SettledReorderContext
  /** Opens the reschedule flow. */
  onReschedule: () => void
  /** Opens the cancellation flow. */
  onCancel: () => void
}

/** One clickable action card, mock-faithful: colored icon chip, semibold title, gray description. */
const ActionCard: FC<{ icon: ReactNode, chipColor: string, title: string, description: string, onClick: () => void }> =
  ({ icon, chipColor, title, description, onClick }) => (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: 1,
        minWidth: '220px',
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        backgroundColor: 'white',
        textAlign: 'start',
        alignItems: 'flex-start',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        <Box
          sx={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            backgroundColor: chipColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant='text-lg' sx={{ fontWeight: 700, color: GRAY_900 }}>{title}</Typography>
        <Typography variant='text-sm' color='text.secondary'>{description}</Typography>
      </Stack>
    </ButtonBase>
  )

/** One label/value recap column; renders nothing when the value is empty. */
const RecapColumn: FC<{ label: string, value: string | null | undefined }> = ({ label, value }) => {
  if (!value) return null
  return (
    <Stack spacing={0.25} sx={{ minWidth: '140px' }}>
      <Typography variant='text-xs' color='text.secondary'>{label}</Typography>
      <Typography variant='text-md' sx={{ fontWeight: 600, color: GRAY_900 }}>{value}</Typography>
    </Stack>
  )
}

/**
 * "Bestellung bearbeiten" full-page content on the Propstack embed order-actions surface (ISSUE-2018).
 *
 * Propstack renders one status tile per order in its Medien grid and opens this page in its modal
 * iframe with the tile's orderId. The page shows up to three action cards above the mock's recap
 * card (title + "Beauftragt am" badge + Referenz/Terminart/Kontakt vor Ort/Zahlungsmethode columns
 * + priced lines + Gesamt). The actions come first (ISSUE-3173): the iframe height is host-capped
 * and the recap grows with the order's line items, so below it the CTAs ended up under the fold.
 * Reschedule and cancel are only offered while the order is still editable
 * (ORDER_PLACED / APPOINTMENT_AGREED).
 *
 * Struck-through public prices are intentionally NOT rendered on the lines: the deal financials
 * carry only the paid prices, not the catalogue original prices (those live in the ISSUE-2019
 * catalogue DTOs).
 */
export const EmbedOrderActions: FC<Props> = ({ order, objectReferenceId, deal, isDealPending, isDealError = false, onNewOrder, reorderContext, onReschedule, onCancel }) => {
  const { t, i18n } = useTranslation(['embed', 'product'])

  const isEditable = EDITABLE_STEPS.includes(order.statusStep)

  // Resolve the billing label only when a translation exists; otherwise pass null so RecapColumn
  // hides the column instead of rendering the raw key (e.g. an unknown/absent billingMethod).
  const billingMethodKey = `status_card.popup.billing_method.${deal?.billingMethod}`
  const billingMethodLabel = deal?.billingMethod && i18n.exists(`embed:${billingMethodKey}`) ? t(billingMethodKey) : null

  const fireAction = (action: PageAction, handler: () => void) => {
    const ids = { orderId: order.orderId, objectReferenceId }
    if (action !== 'NEW_ORDER') {
      logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_ACTION_CLICKED, { action, source: 'ACTIONS_PAGE', ...ids })
      handler()
      return
    }
    // The event logs before the flow opens when the context is cached, after the read otherwise
    const orderAttemptId = startOrderAttempt('status_card')
    withReorderContext(reorderContext(), (context, clickTime) => {
      logAnalyticsEvent(AnalyticsEvent.EMBED_STATUS_CARD_ACTION_CLICKED, {
        action,
        source: 'ACTIONS_PAGE',
        ...ids,
        orderAttemptId,
        ...embedReorderClickProps('status_card', context),
        ...clickTime,
      })
    })
    handler()
  }

  const totalFee = deal?.discountedFeePrice ?? deal?.feePrice

  // Terminart column = the organization product the client picked during ordering (one per order).
  const organizationKind = deal?.assignments
    ?.flatMap((assignment) => assignment.products)
    .find((product) => OrganizationProductKinds.has(product.kind))
    ?.kind

  if (isDealError && !isDealPending) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ py: 8, textAlign: 'center' }} data-testid='embed-order-actions-error'>
        <Typography variant='text-md' color='text.secondary'>{t('status_card.popup.load_error')}</Typography>
      </Stack>
    )
  }

  if (isDealPending || !deal) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ py: 8 }} data-testid='embed-order-actions-loading'>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack spacing={3} data-testid='embed-order-actions'>

      <Stack direction='row' spacing={2} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'stretch' }}>
        <ActionCard
          icon={<AddBoxOutlinedIcon sx={{ color: GREEN_600 }} />}
          chipColor={GREEN_100}
          title={t('status_card.popup.new_order.title')}
          description={t('status_card.popup.new_order.description')}
          onClick={() => fireAction('NEW_ORDER', onNewOrder)}
        />
        {isEditable && (
          <>
            <ActionCard
              icon={<EditCalendarOutlinedIcon sx={{ color: DARK_ORANGE }} />}
              chipColor={YELLOW_100}
              title={t('status_card.popup.reschedule.title')}
              description={t('status_card.popup.reschedule.description')}
              onClick={() => fireAction('RESCHEDULE', onReschedule)}
            />
            <ActionCard
              icon={<CancelOutlinedIcon sx={{ color: CORAL_600 }} />}
              chipColor={CORAL_100}
              title={t('status_card.popup.cancel.title')}
              description={t('status_card.popup.cancel.description')}
              onClick={() => fireAction('CANCEL', onCancel)}
            />
          </>
        )}
      </Stack>

      <Stack
        spacing={2.5}
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          backgroundColor: 'white',
        }}
      >

        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
          <Typography variant='text-md' sx={{ fontWeight: 700, color: GRAY_900 }}>{t('status_card.popup.title')}</Typography>
          <MUIBadge
            color='green'
            size='sm'
            label={t('status_card.popup.ordered_at', { date: moment(order.createdAt).format('LL') })}
          />
        </Stack>

        <Stack direction='row' spacing={4} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <RecapColumn label={t('status_card.popup.reference')} value={deal.reference} />
          <RecapColumn
            label={t('status_card.popup.terminart_label')}
            value={organizationKind ? t(`status_card.popup.terminart.${organizationKind}`) : null}
          />
          <RecapColumn label={t('status_card.popup.contact_label')} value={deal.contactPerson?.name} />
          <RecapColumn
            label={t('status_card.popup.shooting_date')}
            value={order.shootingStartDateTime ? moment(order.shootingStartDateTime).format('LLL') : null}
          />
          <RecapColumn
            label={t('status_card.popup.billing_method_label')}
            value={billingMethodLabel}
          />
        </Stack>

        <Divider />

        <Stack spacing={1}>
          {deal.financials.products.map((product) => (
            <Stack key={product.productId} direction='row' justifyContent='space-between' spacing={2} alignItems='baseline'>
              <Typography variant='text-md' sx={{ color: GRAY_900 }}>
                {product.quantity > 1 && `${product.quantity} x `}
                {t(`product:p_${product.productId}`)}
              </Typography>
              <Typography variant='text-md' sx={{ fontWeight: 600, textAlign: 'end', color: GRAY_900 }}>
                {formatPrice(valueMultiplied(product.unitPrice, product.quantity), product.unitPrice.currency)}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Divider />

        <Stack direction='row' justifyContent='space-between' spacing={2}>
          <Typography variant='text-lg' sx={{ fontWeight: 700, color: GRAY_900 }}>{t('status_card.popup.total')}</Typography>
          <Typography variant='text-lg' sx={{ fontWeight: 700, textAlign: 'end', color: GRAY_900 }}>
            {totalFee ? formatPrice(totalFee, totalFee.currency) : ''}
          </Typography>
        </Stack>

      </Stack>

    </Stack>
  )
}

export default EmbedOrderActions
